import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Tabs from '../../components/ui/Tabs';
import Button from '../../components/ui/Button';
import { PageEmpty } from '../performance-operations/styles';
import { usePanel } from '../performance-operations/config';
import MeSummary from '../performance-me/components/MeSummary';
import {
  PageShell, ShellHeader, TabBody, TabStrip,
} from '../performance-me/styles';
import TeamFilterBar from './components/TeamFilterBar';
import TeamSummary from './components/TeamSummary';
import TeamTable from './components/TeamTable';
import { TEAM_TABS, fetchTeamContext, useTeamFilters } from './config';

// PM Analitik Faz 2 (spec §9) — E17 "Ekibimin Performansı" (/performance/team): yöneticinin kendi ekibi (güncel hiyerarşi).
// Görünme: astı olan (menü requiresUserFlag hasSubordinates) + PM aboneliği + koltuk; astsıza sunucu 403 → sayfa durumu.
// İK/EA olup yönetici olan da yalnız kendi ekibini görür. Sekmeler: Özet | Ekip | Kendi Performansım (= E18 Özet bileşeni, aynı
// `me` ucu; yönetici/İK yetkisi kendi sonucunu erken göstermez). Süzgeçler ve sekme URL'de; tek kaydırma: sekme gövdesi
// (Ekip sekmesinde yalnız tablo satırları).
export default function PerformanceTeamPage() {
  const { t } = useTranslation();
  const { filters, setFilters } = useTeamFilters();
  const contextPanel = usePanel(fetchTeamContext, []);
  const context = contextPanel.data?.context || null;
  const forbidden = !!contextPanel.data?.forbidden;
  const periods = useMemo(() => context?.periods || [], [context]);
  const tab = filters.tab;

  // Dönem zorunlu: URL'de yoksa (ya da artık listede değilse) sunucunun varsayılan dönemi; alt ekip kapalıysa doğrudan ekip.
  useEffect(() => {
    if (!context) return;
    const patch = {};
    if (periods.length > 0 && (!filters.periodId || !periods.some((p) => p.id === filters.periodId))) {
      patch.periodId = context.period?.id || periods[0].id;
    }
    if (filters.team === 'subtree' && !context.skipLevelEnabled) patch.team = 'direct';
    if (Object.keys(patch).length > 0) setFilters((f) => ({ ...f, ...patch }));
  }, [context, periods, filters.periodId, filters.team, setFilters]);

  // Ekip sekmesinin tablo yüksekliği gövdeden ölçülür (tek kaydırma; SurveyDetailPage deseni).
  const bodyRef = useRef(null);
  const [bodyHeight, setBodyHeight] = useState(0);
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => setBodyHeight(entry.contentRect.height));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [context]);

  const tabs = useMemo(() => TEAM_TABS.map((key) => ({ key, label: t(`performance.workspace.team.tabs.${key}`) })), [t]);
  const changeTab = useCallback((key) => setFilters((f) => ({ ...f, tab: key })), [setFilters]);
  const ready = !!context && periods.length > 0 && !!filters.periodId;

  return (
    <PageShell data-page="performance-team">
      <ShellHeader>
        <Breadcrumbs items={[{ label: t('performance.workspace.team.title') }]} />
        {forbidden ? <PageEmpty data-state="forbidden">{t('performance.workspace.team.forbidden')}</PageEmpty> : null}
        {contextPanel.error ? (
          <PageEmpty data-state="error">
            <div>{t('performance.operations.panel.error')}</div>
            <Button variant="outline" onClick={contextPanel.retry} data-action="retry">{t('performance.operations.panel.retry')}</Button>
          </PageEmpty>
        ) : null}
        {context && tab !== 'me' && periods.length > 0 ? (
          <TeamFilterBar
            filters={filters}
            onChange={setFilters}
            periods={periods}
            skipLevelEnabled={!!context.skipLevelEnabled}
            showTaskFilters={tab === 'team'}
          />
        ) : null}
        {context ? (
          <TabStrip>
            <Tabs tabs={tabs} activeTab={tab} onChange={changeTab} />
          </TabStrip>
        ) : null}
      </ShellHeader>
      {context ? (
        <TabBody ref={bodyRef} $scroll={tab !== 'team'} data-tab={tab}>
          {tab !== 'me' && periods.length === 0 ? (
            <PageEmpty data-state="no-periods">{t('performance.workspace.team.noPeriods')}</PageEmpty>
          ) : null}
          {tab === 'summary' && ready ? <TeamSummary filters={filters} context={context} /> : null}
          {tab === 'team' && ready ? <TeamTable filters={filters} context={context} containerHeight={bodyHeight} /> : null}
          {tab === 'me' ? (
            <MeSummary periodId={filters.myPeriodId} onPeriodChange={(id) => setFilters((f) => ({ ...f, myPeriodId: id }))} />
          ) : null}
        </TabBody>
      ) : null}
    </PageShell>
  );
}
